import React, { useRef, useState } from 'react';
import axios from 'axios';
import { X, Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const downloadTemplate = async (format) => {
  try {
    const res = await axios.get(`${API_URL}/promotions/import/template?format=${format}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `modelo-promocao.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('Erro ao baixar modelo');
  }
};

const ImportReport = ({ report }) => (
  <div data-testid="import-report" className="space-y-4">
    <div className="grid grid-cols-3 gap-3 text-center">
      <div className="border border-border p-3">
        <p data-testid="import-report-imported" className="text-2xl font-light">{report.imported}</p>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Importados</p>
      </div>
      <div className="border border-border p-3">
        <p data-testid="import-report-created" className="text-2xl font-light">{report.created.length}</p>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Produtos novos</p>
      </div>
      <div className="border border-border p-3">
        <p data-testid="import-report-errors" className={`text-2xl font-light ${report.errors.length ? 'text-red-600' : ''}`}>{report.errors.length}</p>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Com erro</p>
      </div>
    </div>
    {report.skipped > 0 && (
      <p data-testid="import-report-skipped" className="text-xs text-muted-foreground">
        {report.skipped} linha(s) ignorada(s) — sem preço promocional ou variação de um produto já importado.
      </p>
    )}
    {report.created.length > 0 && (
      <p className="text-xs text-muted-foreground flex gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
        Produtos novos foram cadastrados sem fotos — complete-os em Admin › Produtos.
      </p>
    )}
    {report.errors.length > 0 && (
      <div className="border border-red-200 bg-red-50 max-h-48 overflow-y-auto">
        {report.errors.map((e) => (
          <p key={e.row} data-testid={`import-error-row-${e.row}`} className="text-xs px-3 py-2 border-b border-red-100 last:border-0 flex gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
            <span><strong>Linha {e.row}</strong>{e.sku ? ` (${e.sku})` : ''}: {e.message}</span>
          </p>
        ))}
      </div>
    )}
  </div>
);

export const PromotionImportDialog = ({ promotion, onClose, onImported }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);

  const handleImport = async () => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API_URL}/promotions/${promotion.id}/import`, form);
      setReport(res.data);
      toast.success(`${res.data.imported} produto(s) importado(s)`);
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar planilha');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="promo-import-dialog">
      <div className="bg-background w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex justify-between items-start gap-4">
          <div>
            <p className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground">Importar planilha</p>
            <h2 className="font-heading text-2xl font-light">{promotion.title}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="promo-import-close"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Aceita o mesmo formato da sua planilha de produtos: <strong>Nome</strong>, <strong>SKU</strong>, <strong>Preço</strong> (preço anterior), <strong>Preço promocional</strong>, Categorias, Estoque, Descrição. Também aceita colunas como preco_anterior / preco_promocional.</p>
            <p>Linhas sem preço promocional e variações repetidas são ignoradas. Se o preço anterior estiver vazio, usamos o preço do cadastro. SKUs não encontrados viram produtos novos.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => downloadTemplate('xlsx')} data-testid="download-template-xlsx" className="flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-muted transition-colors">
              <Download className="w-4 h-4" /> Modelo Excel
            </button>
            <button type="button" onClick={() => downloadTemplate('csv')} data-testid="download-template-csv" className="flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-muted transition-colors">
              <Download className="w-4 h-4" /> Modelo CSV
            </button>
          </div>
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="promo-import-dropzone" className="w-full border-2 border-dashed border-border hover:border-primary p-8 flex flex-col items-center gap-2 transition-colors">
            <FileSpreadsheet className="w-8 h-8 text-muted-foreground" strokeWidth={1.25} />
            <span className="text-sm">{file ? file.name : 'Escolher arquivo .xlsx ou .csv'}</span>
          </button>
          <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid="promo-import-file-input"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }} />
          <button type="button" disabled={!file || uploading} onClick={handleImport} data-testid="promo-import-submit"
            className="w-full flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-black transition-colors disabled:opacity-40">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Importando...' : 'Importar produtos'}
          </button>
          {report && <ImportReport report={report} />}
        </div>
      </div>
    </div>
  );
};

export default PromotionImportDialog;
